import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import NotificationCenter from './NotificationCenter';
import {
    Home,
    Star,
    Menu,
    X,
    MessageSquare,
    User,
    LogOut,
    LayoutDashboard,
    CreditCard,
    ShieldCheck,
    ChevronDown,
    Lock,
    Settings,
    Building2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BRAND } from '../config/brand';
import { useBranding } from '../context/BrandingContext';
import { preloadFeaturedData } from '../pages/FeaturedProperties';

const Navbar = () => {
    const { user, logout } = useAuth();
    const { t, currentLang, setLang } = useBranding();
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);

    const handleLogout = () => {
        logout();
        setIsOpen(false);
        setIsDropdownOpen(false);
        navigate('/');
    };

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const navLinks = [

        {
            name: 'Featured',
            path: '/featured',
            icon: Star,
            onMouseEnter: () => {
                const img = new Image();
                img.src = '/images/hero_registry.avif';
                preloadFeaturedData();
            }
        },
        { name: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, role: ['user', 'seller', 'admin', 'superadmin'] },
        { name: 'Support', path: '/support-history', icon: MessageSquare, role: ['seller', 'admin', 'superadmin'] },
        { name: 'Admin', path: '/admin/payments', icon: ShieldCheck, role: ['admin', 'superadmin'] },
    ];

    const dashboardItems = [
        { name: 'Pricing', path: '/pricing', icon: CreditCard, role: ['seller', 'admin', 'superadmin'] },
        { name: 'Portfolio', path: '/portfolio', icon: LayoutDashboard, role: ['seller', 'admin', 'superadmin'] },
        { name: 'Profile', path: '/profile', icon: User },
        { name: 'Change Password', path: '/profile', icon: Lock },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <header className="sticky top-0 z-[100] w-full bg-white/90 backdrop-blur-xl border-b border-slate-100">
            <div className="max-w-7xl mx-auto px-4 md:px-8 h-24 flex items-center justify-between">
                {/* Logo - The Architectural Signature */}
                <Link to="/" onClick={() => setIsOpen(false)} className="flex items-center gap-4 group">
                    <div className="w-12 h-12 overflow-hidden rounded-[0.25rem] flex items-center justify-center shadow-premium transition-transform group-hover:rotate-12 duration-500">
                        <img src={BRAND.logo} alt={BRAND.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex flex-col">
                        <div className="flex items-baseline">
                            <span className="text-xl md:text-2xl font-black text-primary tracking-[0.2em] leading-none">Modern
                                <span className=" text-secondary">Land</span></span>
                        </div>
                        <div className="flex items-center gap-2 mt-1 max-w-[150px] md:max-w-none overflow-hidden">
                            <div className="h-[1px] w-2 md:w-4 bg-secondary shrink-0"></div>
                            <span className="text-[7px] md:text-[8px] font-bold uppercase tracking-[0.1em] md:tracking-[0.2em] text-primary truncate">{t('tagline')}</span>
                            <div className="h-[1px] w-2 md:w-4 bg-secondary shrink-0"></div>
                        </div>
                    </div>
                </Link>

                {/* Desktop Menu - Editorial Style */}
                <div className="hidden lg:flex items-center gap-12">
                    <div className="flex items-center gap-10 border-r border-slate-100 pr-12 mr-4">
                        {navLinks.map((link) => {
                            if (link.role && (!user || !link.role.includes(user.role))) return null;
                            return (
                                <Link
                                    key={link.name}
                                    to={link.path}
                                    onMouseEnter={link.onMouseEnter}
                                    className={`relative flex items-center gap-2.5 text-[11px] font-black uppercase tracking-[0.2em] transition-all group ${isActive(link.path) ? 'text-secondary' : 'text-primary hover:text-secondary'}`}
                                >
                                    <link.icon size={16} className={isActive(link.path) ? 'text-secondary' : 'text-slate-400 group-hover:text-secondary'} strokeWidth={3} />
                                    {link.name}
                                    {isActive(link.path) && (
                                        <motion.div layoutId="navUnderline" className="absolute -bottom-9 left-0 right-0 h-1 bg-secondary rounded-full" />
                                    )}
                                </Link>
                            );
                        })}
                    </div>

                    {/* Language Switcher */}
                    <div className="flex items-center gap-2 border-l border-slate-100 pl-6 ml-4">
                        {['en', 'si', 'ta'].map((lang) => (
                            <button
                                key={lang}
                                onClick={() => setLang(lang)}
                                className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1.5 rounded-md transition-all ${currentLang === lang ? 'bg-primary text-white shadow-lg shadow-primary/20 scale-105' : 'text-slate-400 hover:text-primary hover:bg-slate-50'}`}
                            >
                                {lang}
                            </button>
                        ))}
                    </div>

                    {user ? (
                        <div className="flex items-center gap-6 ml-6">
                            <NotificationCenter />
                            <div className="flex items-center gap-4 bg-light px-5 py-2.5 rounded-2xl border border-slate-100 shadow-sm">
                                <div className="w-8 h-8 bg-primary rounded-xl flex items-center justify-center text-white text-[10px] uppercase font-black">
                                    {user.name.charAt(0)}
                                </div>
                                <span className="text-primary font-black text-[11px] uppercase tracking-widest">
                                    {user.name}
                                </span>
                            </div>
                            <button onClick={handleLogout} aria-label="Logout" className="w-12 h-12 flex items-center justify-center text-accent hover:bg-accent/5 rounded-2xl transition-all border border-transparent hover:border-accent/10">
                                <LogOut size={22} strokeWidth={3} />
                            </button>
                        </div>
                    ) : (
                        <div className="flex items-center gap-6 ml-6">
                            <Link to="/login" className="text-primary hover:text-secondary font-black text-[11px] uppercase tracking-[0.2em] transition-all">Login</Link>
                            <Link to="/register" className="btn-primary py-4 px-10 text-[11px] font-black uppercase tracking-[0.2em]">Register</Link>
                        </div>
                    )}
                </div>

                {/* Mobile Toggle */}
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    aria-label={isOpen ? "Close menu" : "Open menu"}
                    className="lg:hidden w-14 h-14 flex items-center justify-center text-primary bg-light rounded-[1.5rem] border border-slate-100 transition-all active:scale-90"
                >
                    {isOpen ? <X size={28} strokeWidth={3} /> : <Menu size={28} strokeWidth={3} />}
                </button>
            </div>

            {/* Mobile Menu Dropdown */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="lg:hidden bg-white border-t border-slate-50 overflow-y-auto max-h-[calc(100vh-6rem)] custom-scrollbar"
                    >
                        <div className="px-6 py-12 space-y-10 h-screen">
                            {navLinks.map((link) => {
                                if (link.role && (!user || !link.role.includes(user.role))) return null;
                                return (
                                    <Link
                                        key={link.name}
                                        to={link.path}
                                        onMouseEnter={link.onMouseEnter}
                                        onClick={() => setIsOpen(false)}
                                        className={`flex items-center gap-6 text-[13px] font-black uppercase tracking-[0.3em] ${isActive(link.path) ? 'text-secondary' : 'text-primary'}`}
                                    >
                                        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${isActive(link.path) ? 'bg-secondary/10 text-secondary' : 'bg-light text-slate-400'}`}>
                                            <link.icon size={22} strokeWidth={3} />
                                        </div>
                                        {link.name}
                                    </Link>
                                );
                            })}

                            <div className="pt-6 space-y-6">
                                <div className="flex flex-col gap-4">
                                    <span className="text-[9px] font-black uppercase tracking-[0.3em] text-slate-300">Select Language</span>
                                    <div className="flex gap-3">
                                        {['en', 'si', 'ta'].map((lang) => (
                                            <button
                                                key={lang}
                                                onClick={() => {
                                                    setLang(lang);
                                                    setIsOpen(false);
                                                }}
                                                className={`flex-1 py-4 rounded-2xl text-[11px] font-black uppercase tracking-[0.2em] transition-all ${currentLang === lang ? 'bg-primary text-white shadow-xl' : 'bg-light text-primary border border-slate-100'}`}
                                            >
                                                {lang}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="h-px bg-slate-50 w-full"></div>
                                <Link to="/privacy-policy" onClick={() => setIsOpen(false)} className="flex items-center gap-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-secondary transition-colors">
                                    Privacy Policy
                                </Link>
                                <Link to="/terms-of-service" onClick={() => setIsOpen(false)} className="flex items-center gap-4 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 hover:text-secondary transition-colors">
                                    Terms of Service
                                </Link>
                            </div>

                            {user && (
                                <div className="pt-0 flex justify-center">
                                    <NotificationCenter />
                                </div>
                            )}

                            <div className="pt-0 border-t border-slate-50">
                                {user ? (
                                    <div className="space-y-6">
                                        <div className="flex items-center gap-6 bg-light p-6 rounded-[2.5rem] border border-slate-100 shadow-inner">
                                            <div className="w-16 h-16 bg-primary rounded-[1.5rem] flex items-center justify-center text-white font-black text-2xl shadow-xl">
                                                {user.name.charAt(0)}
                                            </div>
                                            <div>
                                                <h4 className="font-black text-primary uppercase tracking-widest text-sm mb-1">{user.name}</h4>
                                                <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest">{user.email}</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={handleLogout}
                                            aria-label="Logout"
                                            className="w-full flex items-center justify-center gap-4 py-6 bg-accent/5 text-accent font-black uppercase tracking-[0.3em] text-[10px] rounded-[2rem] border border-accent/10"
                                        >
                                            <LogOut size={20} strokeWidth={3} />
                                            Terminate Session
                                        </button>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-4">
                                        <Link to="/login" onClick={() => setIsOpen(false)} className="btn-secondary py-6 text-center text-[10px] font-black uppercase tracking-[0.3em]">Login Access</Link>
                                        <Link to="/register" onClick={() => setIsOpen(false)} className="btn-primary py-6 text-center text-[10px] font-black uppercase tracking-[0.3em]">Join Registry</Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
};

export default Navbar;
